import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function EmailVerificationDialog({ open, onClose, userEmail }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: (theme) => theme.shadows[8],
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pt: 4,
          pb: 2,
          px: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Email Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
              border: "2px solid",
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
            }}
          >
            <EmailIcon sx={{ fontSize: 32 }} />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              textAlign: "center",
            }}
          >
            {t("userProfile.checkEmailTitle", "Check Your Email")}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          textAlign: "center",
          px: 4,
          py: 2,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: "text.primary",
            fontWeight: 500,
            mb: 2,
            lineHeight: 1.6,
          }}
        >
          {t(
            "userProfile.checkEmailMessage",
            "A verification link has been sent to your email address."
          )}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            lineHeight: 1.5,
            mb: 2,
          }}
        >
          {t(
            "userProfile.checkEmailDescription",
            "Please check your inbox and click the verification link to complete the process. Don't forget to check your spam folder if you don't see the email."
          )}
        </Typography>

        {/* Email Address Display */}
        {userEmail && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
              border: "1px solid",
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 0.5,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              {t("userProfile.sentToEmail", "Sent to")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "primary.main",
                fontWeight: 600,
                fontSize: "0.9rem",
              }}
            >
              {userEmail}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          px: 4,
          pb: 4,
          pt: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            py: 1.5,
            minWidth: 120,
            boxShadow: (theme) => theme.shadows[2],
            "&:hover": {
              boxShadow: (theme) => theme.shadows[4],
            },
          }}
        >
          {t("common.gotIt", "Got it!")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EmailVerificationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userEmail: PropTypes.string,
};

export default EmailVerificationDialog;
